import { useCallback, useContext } from "react";
import { ClipContext } from "./clip.context";
import { commentOnClip, deleteClip as deleteClipService, incrementViews, deleteComment as deleteCommentService, addReaction as addReactionService } from "./service";
import { useSelector } from "react-redux";
import { selectUser } from "../../../../redux/selectors";

export const useClip = () => {
  const user = useSelector(selectUser);
  const { store } = useContext(ClipContext);
  const state = store();

  const isOwner = state.clip.isOwner;

  const getMessageById = useCallback(
    (messageId: string) => {
      return state.clip.transcript.find(msg => msg.id === messageId);
    },
    [state.clip.transcript],
  );

  const getMessageAtTimestamp = useCallback(
    (timestamp: number): { id: string; index: number } | null => {
      const messages = state.clip.transcript;
      let message: { id: string; index: number } | null = null;

      for (let i = 0; i < messages.length; i++) {
        const time = Number(messages[i].startTime);
        if (time <= timestamp) {
          message = { id: messages[i].id, index: i };
        } else {
          break;
        }
      }
      return message;
    },
    [state.clip.transcript],
  );

  const comment = useCallback(
    async (content: string) => {
      const commentResponse = await commentOnClip(state.clip.id, content);
      state.pushComment({
        id: commentResponse.id,
        comment: commentResponse.content,
        createdAt: commentResponse.createdAt,
        user: `${user?.firstName} ${user?.lastName}`,
      });
    },
    [state.clip.id],
  );

  const deleteComment = useCallback(
    async (commentId: string) => {
      await deleteCommentService(state.clip.id, commentId);
      state.deleteComment(commentId);
    },
    [state.clip.id],
  );

  const deleteClip = useCallback(
    async () => {
      await deleteClipService(state.clip.id);
    },
    [state.clip.id],
  );

  const incrementView = useCallback(
    async () => {
      await incrementViews(state.clip.id);
    },
    [state.clip.id],
  );

  const addReaction = useCallback(
    async (reaction: string, timestamp: number) => {
      await addReactionService(state.clip.id, reaction, timestamp);
      const newReaction = {
        id: Date.now().toString(), // for now we are using this property, so its ok to leave as this for now
        reaction,
        timestamp,
        createdAt: new Date().toISOString(),
        user: `${user?.firstName} ${user?.lastName}`,
      }
      state.pushReaction(newReaction);
      return [...state.clip.reactions, newReaction];
    },
    [state.clip.id],
  );

  return {
    isFetching: state.isFetching,
    fetchError: state.fetchError,
    getMessageById,
    getMessageAtTimestamp,
    comment,
    deleteComment,
    deleteClip,
    incrementView,
    addReaction,
    isOwner,
    clip: {
      id: state.clip.id,
      eventId: state.clip.eventId,
      title: state.clip.title,
      owner: state.clip.owner,
      host: state.clip.host,
      status: state.clip.status,
      views: state.clip.views,
      thumbnailUrl: state.clip.thumbnailUrl,
      videoUrl: state.clip.videoUrl,
      transcript: state.clip.transcript,
      createdAt: state.clip.createdAt,
      comments: state.clip.comments,
      reactions: state.clip.reactions,
    }
  };
};
